// src/utils/ttmQrCalibrate.js
// Client-side calibration from the printed MeasureSmart QR.
// The QR's physical width is a known constant (black-to-black at 100% print),
// so detecting it gives a precise, sub-pixel, training-free scale reference.
//
// Requires: npm install jsqr
import jsQR from "jsqr";

// Measured black-to-black on a real 100%-scale print. Update if the marker
// art changes. THIS is the single most scale-critical constant in the app.
export const QR_WIDTH_INCHES = 2;

/**
 * Detect the calibration QR in an image and derive scale from its known size.
 *
 * @param {HTMLImageElement|HTMLCanvasElement} image  decoded source image
 * @param {object} opts
 *   qrInches        real-world QR width (default QR_WIDTH_INCHES)
 *   maxDetectWidth  downscale cap for fast detection (default 1400)
 *   minQrPx         below this QR pixel width -> lowConfidence (default 80)
 *   expectSubstring if set, only accept QRs whose decoded text contains it
 *                   (e.g. "measuresmart") so stray QRs can't hijack scale
 *   inversionAttempts  jsQR option (default "attemptBoth")
 *
 * @returns null if no (matching) QR is found, otherwise:
 *   {
 *     pxPerInch,            // scale in ORIGINAL image pixels per inch
 *     qrPixelWidth,         // mean side length of the QR, original pixels
 *     corners: { tl, tr, br, bl },   // original image-pixel coordinates
 *     coverage,             // qrPixelWidth / imageWidth (0..1)
 *     decoded,              // decoded QR text
 *     lowConfidence,        // true if the QR is small in frame
 *   }
 */
export function detectQrCalibration(image, opts = {}) {
  const qrInches = opts.qrInches || QR_WIDTH_INCHES;
  const maxDetectWidth = opts.maxDetectWidth || 1400;
  const minQrPx = opts.minQrPx || 80;
  const inversionAttempts = opts.inversionAttempts || "attemptBoth";

  const natW = image.naturalWidth || image.width;
  const natH = image.naturalHeight || image.height;
  if (!natW || !natH) return null;

  // Downscale for fast, reliable detection; corners are rescaled afterward.
  const scale = Math.min(1, maxDetectWidth / natW);
  const w = Math.max(1, Math.round(natW * scale));
  const h = Math.max(1, Math.round(natH * scale));

  let imageData;
  try {
    const cv = document.createElement("canvas");
    cv.width = w; cv.height = h;
    const ctx = cv.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, w, h);
    imageData = ctx.getImageData(0, 0, w, h);
  } catch (e) {
    // Tainted canvas / unsupported source
    return null;
  }

  const result = jsQR(imageData.data, w, h, { inversionAttempts });
  if (!result || !result.location) return null;

  if (
    opts.expectSubstring &&
    (!result.data ||
      !result.data.toLowerCase().includes(opts.expectSubstring.toLowerCase()))
  ) {
    return null; // a QR, but not ours — ignore it
  }

  const inv = 1 / scale; // detection-space -> original pixels
  const P = (p) => ({ x: p.x * inv, y: p.y * inv });
  const loc = result.location;
  const tl = P(loc.topLeftCorner);
  const tr = P(loc.topRightCorner);
  const br = P(loc.bottomRightCorner);
  const bl = P(loc.bottomLeftCorner);

  const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
  // Average all four sides for a robust pixel width of the square.
  const qrPixelWidth =
    (dist(tl, tr) + dist(tr, br) + dist(br, bl) + dist(bl, tl)) / 4;

  if (!qrPixelWidth || !isFinite(qrPixelWidth) || qrPixelWidth < 8) return null;

  return {
    pxPerInch: qrPixelWidth / qrInches,
    qrPixelWidth,
    corners: { tl, tr, br, bl },
    coverage: qrPixelWidth / natW,
    decoded: result.data || "",
    lowConfidence: qrPixelWidth < minQrPx,
  };
}
